import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { RxCollection } from 'rxdb';
import { OfflineStateService } from './offline-state.service';
import { TerminalIdentityService } from '@stockflow/offline-core';
import { PendingQueueService, PendingRequest } from './pending-queue.service';
import { API_CONFIG } from '../../config/api.config';
import { StockFlowDatabase } from '../rxdb-database';
import { setSyncInProgress } from '../../interceptors/auth.interceptor';

const MAX_RETRY = 3;

/**
 * Collections that can have PENDING documents to push to the cloud.
 * Maps RxDB collection name → API endpoint for bulk/individual push.
 */
interface PushTarget {
  collection: string;
  endpoint: string;
  /** If true, post each doc individually. If false, use bulk endpoint. */
  individual: boolean;
}

const PUSH_TARGETS: PushTarget[] = [
  { collection: 'sales_orders', endpoint: `${API_CONFIG.partnersService}/sales-orders`, individual: true },
  { collection: 'purchase_orders', endpoint: `${API_CONFIG.partnersService}/purchase-orders`, individual: true },
  { collection: 'stock_movements', endpoint: `${API_CONFIG.inventoryService}/stock-movements`, individual: true },
  { collection: 'journal_entries', endpoint: `${API_CONFIG.accountingService}/journal-entries`, individual: true },
  { collection: 'leave_requests', endpoint: `${API_CONFIG.hrService}/leave-requests`, individual: true },
  { collection: 'pay_runs', endpoint: `${API_CONFIG.hrService}/pay-runs`, individual: true },
];

export interface SyncPushResult {
  queueDrained: number;
  queueFailed: number;
  collectionsPushed: number;
  collectionsFailed: number;
  details: Array<{ source: string; count: number; status: 'SUCCESS' | 'FAILED'; error?: string }>;
}

@Injectable({ providedIn: 'root' })
export class SyncPushService {
  private _syncing = false;

  constructor(
    private http: HttpClient,
    private offlineState: OfflineStateService,
    private pendingQueue: PendingQueueService,
    private terminalIdentity: TerminalIdentityService,
  ) {}

  get isSyncing(): boolean {
    return this._syncing;
  }

  /**
   * Main sync push: drain pending HTTP queue + push PENDING RxDB docs.
   * Called automatically when connection is restored.
   */
  async pushAll(): Promise<SyncPushResult> {
    if (this._syncing || !navigator.onLine) {
      return { queueDrained: 0, queueFailed: 0, collectionsPushed: 0, collectionsFailed: 0, details: [] };
    }

    this._syncing = true;
    setSyncInProgress(true);
    const result: SyncPushResult = {
      queueDrained: 0,
      queueFailed: 0,
      collectionsPushed: 0,
      collectionsFailed: 0,
      details: [],
    };

    try {
      // Phase 1: Drain the pending HTTP queue (intercepted POST/PUT/DELETE)
      const queueResult = await this.drainPendingQueue();
      result.queueDrained = queueResult.drained;
      result.queueFailed = queueResult.failed;
      result.details.push({
        source: 'pending_queue',
        count: queueResult.drained,
        status: queueResult.failed > 0 ? 'FAILED' : 'SUCCESS',
      });

      // Phase 2: Push PENDING RxDB documents
      const db = this.offlineState.getDb();
      if (db) {
        for (const target of PUSH_TARGETS) {
          const pushResult = await this.pushCollection(db, target);
          result.details.push(pushResult);
          if (pushResult.status === 'SUCCESS') {
            result.collectionsPushed++;
          } else {
            result.collectionsFailed++;
          }
        }
      }

      // Refresh pending count
      await this.offlineState.refreshPendingCount();

      // Write sync log
      await this.writeSyncLog(db, result);

      return result;
    } finally {
      this._syncing = false;
      setSyncInProgress(false);
    }
  }

  /**
   * Drain the localStorage pending queue — replay each request to the server.
   */
  private async drainPendingQueue(): Promise<{ drained: number; failed: number }> {
    const pending = this.pendingQueue.getPending();
    let drained = 0;
    let failed = 0;

    for (const entry of pending) {
      if (!navigator.onLine) break;

      try {
        await this.replayRequest(entry);
        this.pendingQueue.markSynced(entry.id);
        drained++;
      } catch {
        this.pendingQueue.markFailed(entry.id);
        failed++;
        // Skip entries that have exceeded retry limit
        if (entry.retryCount >= MAX_RETRY) {
          console.warn(`[SyncPush] Giving up on request ${entry.id} after ${MAX_RETRY} retries`);
        }
      }
    }

    // Clean up synced entries
    this.pendingQueue.clearSynced();
    return { drained, failed };
  }

  /**
   * Replay a single queued HTTP request.
   */
  private async replayRequest(entry: PendingRequest): Promise<void> {
    /**
     * LA CLE D'IDEMPOTENCE VOYAGE ENFIN.
     *
     * `PendingQueue` fabriquait deja un identifiant unique par requete
     * (`crypto.randomUUID()`), et ne l'envoyait jamais. Le rejeu re-postait
     * donc une requete indiscernable de la premiere.
     *
     * Sur un mouvement de stock, la consequence est un DOUBLE decrement : la
     * premiere tentative avait abouti cote serveur, la reponse s'est perdue
     * en route, le client retente — et le stock descend deux fois. Ce n'est
     * pas rattrapable a posteriori : rien ne distingue le doublon d'une
     * seconde vente legitime.
     *
     * L'identifiant de la file EST la cle : il est stable d'un rejeu a
     * l'autre, ce qui est exactement la propriete demandee.
     */
    const headers = new HttpHeaders({
      ...entry.headers,
      'x-idempotency-key': entry.id,
    });

    switch (entry.method) {
      case 'POST':
        await firstValueFrom(this.http.post(entry.url, entry.body, { headers }));
        break;
      case 'PUT':
        await firstValueFrom(this.http.put(entry.url, entry.body, { headers }));
        break;
      case 'PATCH':
        await firstValueFrom(this.http.patch(entry.url, entry.body, { headers }));
        break;
      case 'DELETE':
        await firstValueFrom(this.http.delete(entry.url, { headers }));
        break;
      default:
        console.warn(`[SyncPush] Unknown method: ${entry.method}`);
    }
  }

  /**
   * Push PENDING documents from a RxDB collection to the cloud API.
   */
  private async pushCollection(
    db: StockFlowDatabase,
    target: PushTarget,
  ): Promise<{ source: string; count: number; status: 'SUCCESS' | 'FAILED'; error?: string }> {
    const collection = (db.collections as Record<string, RxCollection>)[target.collection];
    if (!collection) {
      return { source: target.collection, count: 0, status: 'SUCCESS' };
    }

    try {
      const pendingDocs = await collection.find({ selector: { syncStatus: 'PENDING' } }).exec();
      if (pendingDocs.length === 0) {
        return { source: target.collection, count: 0, status: 'SUCCESS' };
      }

      let synced = 0;
      for (const doc of pendingDocs) {
        if (!navigator.onLine) break;

        const data = doc.toJSON() as Record<string, unknown>;
        try {
          // Post to the cloud endpoint with the offlineId for idempotence
          await firstValueFrom(this.http.post(target.endpoint, {
            ...data,
            _offlineSync: true,
          }));

          // Mark as synced in RxDB
          await doc.incrementalPatch({ syncStatus: 'SYNCED' });
          synced++;
        } catch (err: unknown) {
          // Mark as failed but don't stop — continue with next doc
          try {
            await doc.incrementalPatch({ syncStatus: 'FAILED' });
          } catch {
            // If patch fails, skip
          }
          console.warn(`[SyncPush] Failed to push ${target.collection} doc ${data['id']}:`, err);
        }
      }

      return {
        source: target.collection,
        count: synced,
        status: synced === pendingDocs.length ? 'SUCCESS' : 'FAILED',
      };
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      return { source: target.collection, count: 0, status: 'FAILED', error: errorMsg };
    }
  }

  private async writeSyncLog(
    db: StockFlowDatabase | null,
    result: SyncPushResult,
  ): Promise<void> {
    if (!db) return;
    try {
      const totalPushed = result.queueDrained + result.details.reduce((sum, d) => sum + d.count, 0);
      await db.sync_log.insert({
        id: crypto.randomUUID(),
        tableName: '__push_all__',
        direction: 'PUSH',
        recordCount: totalPushed,
        status: result.queueFailed === 0 && result.collectionsFailed === 0 ? 'SUCCESS' : 'PARTIAL',
        errorMessage: null,
        userId: null,
        // Le journal de synchronisation identifiait le poste par null : un
        // incident etait donc impossible a rattacher a une caisse. Ecart Y4.
        terminalId: this.terminalIdentity.code(),
        durationMs: null,
        syncedAt: new Date().toISOString(),
      });
    } catch {
      // Non-critical — don't fail sync for logging errors
    }
  }
}
